// crewbit-admin/src/components/money/settings/NotificationToggleRow.tsx
//
// Single notification toggle row primitive. Label + description + Switch +
// SaveConfirmationPill. Inline-save with 1.5s debounce per Decision §Settings.D3.
// Toggle change immediately reflects locally; debounced save fires after the
// idle window; pill flashes on success/failure.
//
// Mirrors crewbit-mobile/components/money/settings/NotificationToggleRow.tsx
// with web Switch borrowed from NotificationSection's Toggle pattern.

import { useEffect, useRef, useState } from 'react';
import { palette, spacing, transition, typography } from '../../../styles/tokens';
import SaveConfirmationPill from './SaveConfirmationPill';

const DEBOUNCE_MS = 1500;
const PILL_VISIBLE_MS = 2000;

export type NotificationToggleSaveResult =
  | { success: true }
  | { success: false; message?: string };

interface NotificationToggleRowProps {
  label: string;
  description?: string;
  value: boolean;
  /** Called after the 1.5s debounce window. Caller wraps the substrate
   *  mutation hook + returns success or warning state for the pill. */
  onSave: (next: boolean) => Promise<NotificationToggleSaveResult>;
  disabled?: boolean;
}

export default function NotificationToggleRow({
  label,
  description,
  value,
  onSave,
  disabled = false,
}: NotificationToggleRowProps) {
  const [localValue, setLocalValue] = useState<boolean>(value);
  const [pillVisible, setPillVisible] = useState(false);
  const [pillKind, setPillKind] = useState<'success' | 'warning'>('success');
  const [pillLabel, setPillLabel] = useState<string | undefined>(undefined);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pillTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mountedRef = useRef<boolean>(true);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional sync of upstream value into local draft after refetch
    setLocalValue((prev) => (prev === value ? prev : value));
  }, [value]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (pillTimeoutRef.current) clearTimeout(pillTimeoutRef.current);
    };
  }, []);

  const flashPill = (kind: 'success' | 'warning', flashLabel?: string) => {
    if (pillTimeoutRef.current) clearTimeout(pillTimeoutRef.current);
    setPillKind(kind);
    setPillLabel(flashLabel);
    setPillVisible(true);
    pillTimeoutRef.current = setTimeout(() => {
      if (mountedRef.current) setPillVisible(false);
    }, PILL_VISIBLE_MS);
  };

  const handleToggle = () => {
    if (disabled) return;
    const next = !localValue;
    setLocalValue(next);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      const result = await onSave(next);
      if (!mountedRef.current) return;
      if (result.success) flashPill('success');
      else flashPill('warning', result.message);
    }, DEBOUNCE_MS);
  };

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: spacing.md,
        paddingInline: spacing.md,
        paddingBlock: spacing.md,
      }}
    >
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: spacing.xxs }}>
        <div style={{ fontSize: typography.size.subheading, fontWeight: 600, color: palette.g800 }}>
          {label}
        </div>
        {description && (
          <div style={{ fontSize: typography.size.caption, color: palette.g500, lineHeight: 1.4 }}>
            {description}
          </div>
        )}
        <div style={{ minHeight: 18, marginTop: spacing.xxs }}>
          <SaveConfirmationPill visible={pillVisible} kind={pillKind} label={pillLabel} />
        </div>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={localValue}
        aria-label={label}
        disabled={disabled}
        onClick={handleToggle}
        style={{
          width: 40,
          height: 22,
          borderRadius: 11,
          border: 'none',
          cursor: disabled ? 'not-allowed' : 'pointer',
          background: localValue ? palette.accent : palette.g200,
          position: 'relative',
          transition: `background ${transition.fast}`,
          flexShrink: 0,
          opacity: disabled ? 0.5 : 1,
        }}
      >
        <span
          style={{
            display: 'block',
            width: 18,
            height: 18,
            borderRadius: 9,
            background: palette.white,
            position: 'absolute',
            top: 2,
            left: localValue ? 20 : 2,
            transition: `left ${transition.fast}`,
            boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
          }}
        />
      </button>
    </div>
  );
}
